/* eslint-disable @typescript-eslint/dot-notation */
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams, StartEditingCellParams, Column, IRowNode } from 'ag-grid-community';
import { FormControl } from '@angular/forms';
import { flatten, uniq } from 'lodash-es';
import {
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/constraint';
import { Subject, takeUntil } from 'rxjs';
/**
 * Editor Custom AgGrid for Seelct Input.
 *
 * Params:
 * - dataSource: Array<any> (Mandatory) it defines option list, can be string or object array.
 * - viewValueFieldName: (Mandatory when datasource is Array<object>) it allows to specify which object property to show in drop down list.
 * - valueFieldName. (Optional) it allows to specify which object property to give back(ngModel).If it is null or undefined, the component gives back all the object.
 * - multi: boolean (Optional default value false) it allows multi selection.
 * - required: (Optional) It allows to specify required validator
 * - isResettable: (Optional) It allows to reset the cell value
 */
export interface IComboCelleEditorParams {
    idDataSource?: string;
    dataSource: any[] | (() => any[]);
    required?: boolean;
    viewValueFieldName?: string;
    valueFieldName?: string;
    viewFieldName?: string;
    multi?: boolean;
    isResettable?: boolean;
    isArray?: boolean;
    configuratorService?: SeConstraintConfiguratorService;
    constraintFilterService?: SeConstraintApplierService;
    constraintNotifier?: SeConstraintNotifierService;
}
export type ComboCellEditorParams = ICellEditorParams & IComboCelleEditorParams;

@Component({
    selector: 'se-combo-cell-editor',
    templateUrl: './combo-cell-editor.component.html',
    styleUrls: ['./combo-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ComboCellEditorComponent implements ICellEditorAngularComp, OnDestroy {
    // i parametri di input si recuperano in agInit da params es, params['max']
    public dataSource: any[] = [];
    public viewValueFieldName: string = '';
    public valueFieldName: string = '';
    public multi: boolean = false;
    public required: boolean = false;
    public isResettable: boolean = false;
    private configuratorService: SeConstraintConfiguratorService | null = null;
    private constraintFilterService: SeConstraintApplierService | null = null;
    private constraintNotifier: SeConstraintNotifierService | null = null;

    value: any;
    params: ComboCellEditorParams | null = null;
    cellStartEdited: boolean = false;
    cellWidth: string = '';
    formControl: FormControl | null = null;
    inlineEditor: boolean = false;
    isStringArray: boolean = false;

    private destroyed$ = new Subject<void>();

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    protected handleFilterNotification(constraintId: string) {
        if (!this.params) return;
        this.params.data?.select();
        if (!!this.constraintFilterService)
            this.dataSource = this.constraintFilterService.applyFilter(constraintId) ?? [];
    }

    agInit(params: ComboCellEditorParams): void {
        this.params = params;
        this.value = params.value;
        this.cellWidth = params.column.getActualWidth() + 'px';
        if (!!this.params.configuratorService) this.configuratorService = this.params.configuratorService;
        if (!!this.params.constraintFilterService) this.constraintFilterService = this.params.constraintFilterService;
        if (!!this.params.constraintNotifier) this.constraintNotifier = this.params.constraintNotifier;

        // Recupero parametri input
        this.configCellEditor();
        this.inlineEditor = false;

        this.cellStartEdited = this.params.cellStartedEdit;
        if (!!this.configuratorService && !!this.constraintNotifier) {
            const constraints = uniq(
                flatten(
                    this.configuratorService.constraintSources
                        .getSourcesByListName(this.params.colDef.cellEditorParams?.idDataSource ?? '')
                        .map((s) => s.constraints),
                ),
            );

            const dataBindingContext = this.params.data.path.toString();

            for (const c of constraints) {
                this.constraintNotifier
                    .register(c.id, dataBindingContext)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((_) => this.handleFilterNotification(c.id));
            }
        }
    }

    getValue(): any {
        if (this.formControl && !this.formControl.invalid) {
            return this.formControl.value;
        }
        return null;
    }

    isPopup(): boolean {
        return true;
    }

    _onSelectrionClosed(): void {
        if (!this.params) return;
        this.params.api.stopEditing();
    }

    _onFormReady(editorFromControl: FormControl) {
        this.formControl = editorFromControl;
    }

    private configCellEditor() {
        if (!this.params) return;
        if (this.isFunction(this.params.dataSource)) {
            this.dataSource = this.params.dataSource();
        } else {
            this.dataSource = this.params.dataSource || [];
        }

        this.viewValueFieldName = this.params.viewValueFieldName ?? '';
        this.valueFieldName = this.params.valueFieldName ?? '';
        this.multi = !!this.params.multi;

        if (this.dataSource.length > 0) {
            this.viewValueFieldName = this.dataSource[0].hasOwnProperty(this.viewValueFieldName)
                ? this.viewValueFieldName
                : '';
            this.valueFieldName = this.dataSource[0].hasOwnProperty(this.valueFieldName) ? this.valueFieldName : '';
        } else {
            console.error('dataSource is not Array: ', this.dataSource);
        }
        this.required = !!this.params.required;
        this.isResettable = !!this.params.isResettable;
        //this.isResettable = true;
    }

    private isFunction(functionToCheck: unknown): functionToCheck is Function {
        return !!functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    tabPressed() {
        setTimeout(() => {
            if (!this.params || !this.params.api) return;
            const col = this.params.api.getDisplayedColAfter(this.params.column);
            if (!col) return;
            let nextCellCoo: StartEditingCellParams | null = null,
                nextCol = this.gettheFirstEditableCol(col),
                nextRow = this.getNextRow(this.params.node);
            if (nextCol) {
                nextCellCoo = {
                    colKey: nextCol,
                    rowIndex: this.params.rowIndex,
                    rowPinned: this.params.node.rowPinned || null,
                };
            } else if (nextRow && !nextCol) {
                const firstCol = this.params.api.getColumns()?.[0];
                if (!firstCol) return;
                nextCellCoo = {
                    colKey: this.gettheFirstEditableCol(firstCol) ?? '',
                    rowIndex: nextRow.rowIndex ?? -1,
                    rowPinned: nextRow.rowPinned ?? null,
                };
            }
            if (nextCellCoo) {
                this.params.api.startEditingCell(nextCellCoo);
            }
        }, 100);
    }

    //getFirstEditableCol(startCol: Column): Column {
    //    if (!startCol) {
    //        return null;
    //    }
    //    let output = startCol;
    //    while (1 > 0) {
    //        if (!output || output.getColDef().editable) {
    //            break;
    //        }
    //        output = this.params.columnApi.getDisplayedColAfter(output)
    //    }
    //    return output;
    //}

    gettheFirstEditableCol(startCol: Column): Column | null {
        if (!this.params || !this.params.api || !startCol) {
            return null;
        }
        if (startCol.getColDef().editable) return startCol;
        const col = this.params.api.getDisplayedColAfter(startCol);
        if (!col) return null;
        return this.gettheFirstEditableCol(col);
    }

    getNextRow(actualRow: IRowNode): IRowNode | null {
        if (!this.params) return null;
        const nextIndexNumber = Number(actualRow.id);
        if (isNaN(nextIndexNumber)) {
            return null;
        }
        return this.params.api?.getRowNode((nextIndexNumber + 1).toString()) ?? null;
    }
}
